import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import {
  FaTachometerAlt,
  FaBoxes,
  FaShoppingCart,
  FaCashRegister,
  FaShoppingBasket,
  FaTags,
  FaCog,
  FaUserCircle,
  FaSignOutAlt,
  FaMoon,
  FaSun,
  FaClock
} from 'react-icons/fa';
import { authActions } from '../../sagas/authSlice';
import { ROLE_SUPER_ADMIN } from '../../helpers/constants';
import { POS_PAGE } from '../../routes/routeConstants';
import './ModuleSelector.css';

const ModuleSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fullName, businessName, role } = useSelector(state => state.auth);

  const [darkMode, setDarkMode] = useState(false);

  const modules = useMemo(() => [
    ...(role === ROLE_SUPER_ADMIN
      ? [{ title: t('modules.dashboard.title'), icon: FaTachometerAlt, route: '/dashboard', group: 'Pilotage' }]
      : []),
    { title: t('modules.sales.title'), icon: FaShoppingCart, route: '/sales-module', group: 'Gestion' },
    { title: t('modules.purchases.title'), icon: FaShoppingBasket, route: '/purchases-module', group: 'Gestion' },
    { title: t('modules.inventory.title'), icon: FaBoxes, route: '/inventory-module', group: 'Gestion' },
    { title: t('modules.pos.title'), icon: FaShoppingCart, route: '/pos-module', group: 'Caisse' },
    { title: t('modules.posCashier.title', 'POS Caisse'), icon: FaCashRegister, route: POS_PAGE, group: 'Caisse', primary: true },

    // ✅ NEW SHIFTS MODULE
    { title: t('modules.shifts.title'), icon: FaClock, route: '/shifts-module', group: 'Caisse' },
    { title: t('modules.promotion.title', 'Promotions'), icon: FaTags, route: '/promotion-module', group: 'Caisse' },

    { title: t('modules.settings.title'), icon: FaCog, route: '/settings-module', group: 'Administration' },
  ], [role, t]);

  const moduleGroups = useMemo(() => (
    modules.reduce((groups, module, index) => {
      const currentGroup = groups.find(group => group.title === module.group);
      const moduleWithShortcut = { ...module, shortcut: index + 1 };

      if (currentGroup) {
        currentGroup.items.push(moduleWithShortcut);
        return groups;
      }

      return [...groups, { title: module.group, items: [moduleWithShortcut] }];
    }, [])
  ), [modules]);

  /* ===== Keyboard shortcuts 1–9 ===== */
  useEffect(() => {
    const onKey = (e) => {
      const idx = Number(e.key) - 1;
      if (idx >= 0 && idx < modules.length) {
        navigate(modules[idx].route);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modules, navigate]);

  /* ===== Dark mode ===== */
  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  const handleLogout = () => {
    Swal.fire({
      title: 'Confirmer la déconnexion',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Se déconnecter',
      cancelButtonText: 'Annuler'
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(authActions.logout());
        localStorage.clear();
        navigate('/login');
      }
    });
  };

  return (
    <div className="module-selector-wrapper">

      {/* HERO */}
      <div className="hero-section">
        <Container className="hero-content-centered">
          <div className="hero-heading">
            <span className="hero-kicker">Matjar POS</span>
            <h2 className="hero-title">
              {t('modules.welcome', { businessName })}
            </h2>
          </div>

          <div className="hero-right">
            <button
              className="theme-toggle"
              onClick={() => setDarkMode(v => !v)}
            >
              {darkMode ? <FaSun /> : <FaMoon />}
            </button>

            <div className="user-pill">
              <FaUserCircle />
              <span>{fullName}</span>
            </div>

            <button className="logout-btn" onClick={handleLogout}>
              <FaSignOutAlt />
              <span>Déconnexion</span>
            </button>
          </div>
        </Container>
      </div>

      {/* MODULES */}
      <Container className="modules-container">
        <div className="modules-shell">
          {moduleGroups.map((group) => (
            <section className="module-group" key={group.title}>
              <div className="module-group-header">
                <h3>{group.title}</h3>
              </div>

              <div className="modules-row auto-scale">
                {group.items.map((m) => (
                  <Card
                    key={m.route}
                    role="button"
                    tabIndex={0}
                    className={`module-card-dense ${m.primary ? 'primary' : ''}`}
                    onClick={() => navigate(m.route)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(m.route);
                      }
                    }}
                  >
                    <CardBody className="module-card-body-dense">
                      <span className="shortcut">{m.shortcut}</span>
                      <span className="module-icon-shell">
                        <m.icon size={28} />
                      </span>
                      <div className="module-title-dense">{m.title}</div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      </Container>

      {/* FOOTER */}
      <footer className="app-footer">
        <Container className="footer-content">
          <span><strong>{businessName}</strong> — ERP & POS</span>
          <span>© {new Date().getFullYear()}</span>
          <span>Powered by <strong>IDERACLOUD</strong></span>
        </Container>
      </footer>

    </div>
  );
};

export default ModuleSelector;
